<template>
  <form id="addresses" @submit.prevent="validateAndSubmit">
    <div class="grid-row">
      <div class="small-12 large-6 columns">
        <h2>Bezoekadres</h2>
        <visit-address-sub-form
          ref="visitAddressSubForm"
          :address="company.visitAddress"
          :address-type="addressTypes.ADDRESS_TYPE_VISIT"
          :required="isVisitAddressRequired()"
        />
      </div>
      <div class="small-12 large-6 columns">
        <h2>Postadres</h2>
        <post-address-sub-form
          ref="postAddressSubForm"
          :address="company.postAddress"
          :address-type="addressTypes.ADDRESS_TYPE_POST"
          :required="isPostAddressRequired()"
        />
      </div>
    </div>
    <div class="grid-row">
      <div class="small-12 columns">
        <h2>Factuuradres </h2>
        <p>
          Vul hieronder uw factuuradres in. Indien het factuuradres niet overeenkomt met uw bezoek- of postadres kiest
          u voor ‘Afwijkend’. Postbus of huisnummer en straat zijn dan verplicht in te vullen velden.
        </p>
      </div>
    </div>
    <div class="grid-row">
      <div class="small-12 large-6 columns">
        <div class="formSectionCompound">
          <div class="jq_gridcontentarea">
            <select-field
              id="primaryAddress-primaryAddressSameAs"
              :$v="$v.company.primaryAddress.isSameAsAddress"
              :value="company.primaryAddress.isSameAsAddress"
              :options="primaryAddressSameAsOptions"
              :required="true"
              name="primaryAddress[primaryAddressSameAs]"
              label="Is gelijk aan *"
              @change="(v) => setCompanyAddressProperty(addressTypes.ADDRESS_TYPE_PRIMARY, 'isSameAsAddress', v)"
            />
          </div>
          <div class="jq_gridcontentarea">
            <input-field
              id="primaryAddress-department"
              :$v="$v.company.primaryAddress.department"
              :value="company.primaryAddress.department"
              :required="false"
              :maxlength="32"
              :normalization-filter="filters.capitalize"
              :label="$t('companyForm.fieldName.department')"
              name="primaryAddress[department]"
              @input="(v) => setCompanyAddressProperty(addressTypes.ADDRESS_TYPE_PRIMARY, 'department', v)"
            />
          </div>
          <div class="jq_gridcontentarea">
            <input-field
              id="primaryAddress-firstName"
              :$v="$v.company.primaryAddress.firstName"
              :value="company.primaryAddress.firstName"
              :required="false"
              :maxlength="12"
              :normalization-filter="filters.initials"
              name="primaryAddress[firstName]"
              label="Voorletter"
              @input="(v) => setCompanyAddressProperty(addressTypes.ADDRESS_TYPE_PRIMARY, 'firstName', v)"
            />
          </div>
          <div class="jq_gridcontentarea">
            <input-field
              id="primaryAddress-surnamePrefix"
              :$v="$v.company.primaryAddress.surnamePrefix"
              :value="company.primaryAddress.surnamePrefix"
              :required="false"
              :maxlength="8"
              name="primaryAddress[surnamePrefix]"
              label="Tussenvoegsel"
              @input="(v) => setCompanyAddressProperty(addressTypes.ADDRESS_TYPE_PRIMARY, 'surnamePrefix', v)"
            />
          </div>
          <div class="jq_gridcontentarea">
            <input-field
              id="primaryAddress-surname"
              :$v="$v.company.primaryAddress.surname"
              :value="company.primaryAddress.surname"
              :required="false"
              :maxlength="50"
              :normalization-filter="filters.capitalize"
              name="primaryAddress[surname]"
              label="Achternaam"
              @input="(v) => setCompanyAddressProperty(addressTypes.ADDRESS_TYPE_PRIMARY, 'surname', v)"
            />
          </div>
          <div class="jq_gridcontentarea">
            <select-field
              id="primaryAddress-gender"
              :$v="$v.company.primaryAddress.gender"
              :value="company.primaryAddress.gender"
              :options="salutationOptions"
              :required="true"
              name="primaryAddress[gender]"
              label="Aanhef *"
              @change="(v) => setCompanyAddressProperty(addressTypes.ADDRESS_TYPE_PRIMARY, 'gender', v)"
            />
          </div>
          <div class="jq_gridcontentarea">
            <input-field
              id="primaryAddress-emailAddress"
              :$v="$v.company.primaryAddress.emailAddress"
              :value="company.primaryAddress.emailAddress"
              :required="true"
              :maxlength="80"
              name="primaryAddress[emailAddress]"
              label="E-mailadres"
              @input="(v) => setCompanyAddressProperty(addressTypes.ADDRESS_TYPE_PRIMARY, 'emailAddress', v)"
            />
          </div>
        </div>
      </div>
      <div class="small-12 large-6 columns">
        <visit-address-sub-form
          v-if="company.primaryAddress.isSameAsAddress === addressTypes.ADDRESS_TYPE_VISIT"
          :address="company.visitAddress"
          :address-type="addressTypes.ADDRESS_TYPE_VISIT"
          :disable-fields="true"
          :required="true"
        />
        <post-address-sub-form
          v-if="company.primaryAddress.isSameAsAddress === addressTypes.ADDRESS_TYPE_POST"
          :address="company.postAddress"
          :address-type="addressTypes.ADDRESS_TYPE_POST"
          :disable-fields="true"
          :required="true"
        />
        <primary-address-sub-form
          v-if="company.primaryAddress.isSameAsAddress === addressTypes.ADDRESS_TYPE_PRIMARY"
          ref="primaryAddressSubForm"
          :address="company.primaryAddress"
          :address-type="addressTypes.ADDRESS_TYPE_PRIMARY"
          :disable-fields="false"
          :required="true"
        />
      </div>
    </div>
    <div class="grid-row">
      <fieldset class="submit">
        <a href="#" class="button alt cancel-company-edit" @click.prevent="resetForm">Annuleren</a>
        <input id="submit-company-data" type="submit" name="submit-company-data" value="Opslaan">
      </fieldset>
    </div>
  </form>
</template>

<script>
import InputField from './fields/InputField'
import SelectField from './fields/SelectField'
import VisitAddressSubForm from './VisitAddressSubForm'
import PostAddressSubForm from './PostAddressSubForm'
import PrimaryAddressSubForm from './PrimaryAddressSubForm'
import ValidationMessageList from './ValidationMessageList'
import * as companyMutationTypes from '../../store/modules/company/mutation-types'
import * as genderTypes from '../../types/gender'
import * as addressTypes from '../../types/address-type'
import { required, maxLength, email } from 'vuelidate/lib/validators'
import { initials, surname } from '../../validators'
import customFilters from '../../filters'
import InitialsInputField from './fields/InitialsInputField'
import ShowAlertErrorHandler from '../../mixins/show-alert-error-handler'

export default {
  name: 'CompanyAddressesForm',
  components: {
    InitialsInputField,
    InputField,
    SelectField,
    VisitAddressSubForm,
    PostAddressSubForm,
    PrimaryAddressSubForm,
    ValidationMessageList
  },
  mixins: [ShowAlertErrorHandler],
  props: {
    company: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      salutationOptions: [
        { value: '', text: this.$t('generic.select') },
        { value: genderTypes.GENDER_MALE, text: this.$t('generic.gender.M') },
        { value: genderTypes.GENDER_FEMALE, text: this.$t('generic.gender.F') },
        { value: genderTypes.GENDER_UNKNOWN, text: this.$t('generic.gender.U') }
      ],
      primaryAddressSameAsOptions: [
        { value: '', text: this.$t('generic.select') },
        { value: addressTypes.ADDRESS_TYPE_POST, text: 'Postadres' },
        { value: addressTypes.ADDRESS_TYPE_VISIT, text: 'Bezoekadres' },
        { value: addressTypes.ADDRESS_TYPE_PRIMARY, text: 'Geen selectie - Afwijkend' }
      ],
      addressTypes,
      filters: {
        capitalize: customFilters.capitalize,
        upperCase: customFilters.upperCase,
        digits: customFilters.digits,
        initials: customFilters.initials
      }
    }
  },
  validations () {
    return {
      company: {
        primaryAddress: {
          isSameAsAddress: {
            required
          },
          firstName: {
            maxLength: maxLength(12),
            initials
          },
          surnamePrefix: {
            maxLength: maxLength(8),
            surname
          },
          surname: {
            maxLength: maxLength(50),
            surname
          },
          gender: {
            required
          },
          department: {
            maxLength: maxLength(32)
          },
          emailAddress: {
            maxLength: maxLength(80),
            email
          }
        }
      }
    }
  },
  methods: {
    isVisitAddressRequired () {
      return this.company.primaryAddress.isSameAsAddress === addressTypes.ADDRESS_TYPE_VISIT
    },
    isPostAddressRequired () {
      return this.company.primaryAddress.isSameAsAddress === addressTypes.ADDRESS_TYPE_POST
    },
    setCompanyAddressProperty (addressType, property, value) {
      this.company[addressTypes.Mapping[addressType]][property] = value
    },
    setPrimaryAddress () {
      if (this.company.primaryAddress.isSameAsAddress === addressTypes.ADDRESS_TYPE_POST) {
        this.company.primaryAddress.houseNumber = null
        this.company.primaryAddress.houseNumberExtension = null
        this.company.primaryAddress.street = null
        Object.assign(this.company.primaryAddress, {}, this.company.postAddress)
      }
      if (this.company.primaryAddress.isSameAsAddress === addressTypes.ADDRESS_TYPE_VISIT) {
        this.company.primaryAddress.postOfficeBox = null
        Object.assign(this.company.primaryAddress, {}, this.company.visitAddress)
      }
    },
    validateAndSubmit () {
      if (!this.validate()) {
        window.scrollTo(0, 0)
        return false
      }
      this.submit()
    },
    validate () {
      this.$v.$touch()
      this.$refs.visitAddressSubForm.touchValidation()
      this.$refs.postAddressSubForm.touchValidation()
      this.company.primaryAddress.isSameAsAddress === addressTypes.ADDRESS_TYPE_PRIMARY &&
        this.$refs.primaryAddressSubForm.touchValidation()

      if (this.$v.$invalid ||
        this.$refs.visitAddressSubForm.isInvalid() ||
        this.$refs.postAddressSubForm.isInvalid() ||
        (
          this.company.primaryAddress.isSameAsAddress === addressTypes.ADDRESS_TYPE_PRIMARY &&
          this.$refs.primaryAddressSubForm.isInvalid()
        )
      ) {
        return false
      }
      return true
    },
    async submit () {
      this.setPrimaryAddress()
      try {
        await this.$store.dispatch('company/submitCompanyAddresses', this.company)
        this.$store.commit(`company/${companyMutationTypes.SET_COMPANY_ADDRESSES}`, this.company)
        this.showAlert('Adresgegevens opgeslagen')
        this.$v.$reset()
      } catch (error) {
        if (error.validationErrorResult && error.validationErrorResult.validationErrors) {
          error.validationErrorResult.validationErrors.forEach(propertyError => {
            this.showAlert(this.$te(`companyForm.validation['${propertyError.code}']`)
              ? this.$t(
                `companyForm.validation['${propertyError.code}']`,
                { fieldName: this.$t(`companyForm.fieldName['${propertyError.path}']`) }
              ) : propertyError.message
            )
          })
        } else {
          this.showAlertForErrorAndRedirectToRoute(error, {}, {401: {'name': 'login'}})
        }
      }
    },
    resetForm () {
      this.$emit('cancel', {})
    }
  }
}
</script>

<style scoped>

</style>



// WEBPACK FOOTER //
// src/components/forms/CompanyAddressesForm.vue